import { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@/components/hooks/use-toast';

import {
  resetClientStatus,
  selectClientStatus,
  selectSessionAdvisor,
  selectClientDeletionStatus,
  putClientByClientIdRequest,
  deleteClientByClientIdRequest,
  fetchClientDeletionEligibilityByClientIdRequest,
} from '@client/model';
import { ActionStatus, ClientStatus } from '@shared';

export interface DeleteClientProps {
  open: boolean;
  client: Client;
  onClose: () => void;
}

export function useDeleteClient({ open, onClose, client }: DeleteClientProps) {
  const { toast } = useToast();
  const dispatch = useDispatch();

  const status = useSelector(selectClientStatus);
  const advisor = useSelector(selectSessionAdvisor);
  const deletionStatus = useSelector(selectClientDeletionStatus);
  const [ccpaDeletionModalState, setCCPADeletionModalState] = useState(false);

  const isLoading = status === ActionStatus.loading;
  const isDeleted = status === ActionStatus.deleted;
  const isDeleteFailed = status === ActionStatus.deleteFailed;
  const isArchived = status === ActionStatus.archived;

  useEffect(() => {
    if (client) {
      dispatch(fetchClientDeletionEligibilityByClientIdRequest(client.clientId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  useEffect(() => {
    if (open && (isArchived || isDeleted)) {
      toast({
        title: 'Success!',
        variant: 'default',
        description: isArchived ? 'Client has been archived.' : 'Client has been deleted.',
      });

      onClose();
      setCCPADeletionModalState(false);
      dispatch(resetClientStatus());
    }
    if (open && isDeleteFailed) {
      toast({
        title: 'Error',
        variant: 'destructive',
        description:
          'An error occurred while deleting the client. Please try again or contact support.',
      });
      onClose();
      dispatch(resetClientStatus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArchived, isDeleted, open, isDeleteFailed]);

  function handleDeleteClient() {
    if (!isLoading) {
      dispatch(
        deleteClientByClientIdRequest({
          clientId: client.clientId,
          advisorId: advisor.advisorId,
          ccpaDeletionModalState,
        }),
      );
    }
  }

  function handleArchiveClient() {
    if (!isLoading) {
      dispatch(
        putClientByClientIdRequest({
          ...client,
          clientStatus: ClientStatus.archived,
        }),
      );
    }
  }

  const clientName = `${client.clientFirstName} ${client.clientLastName}`;

  const showCCPADeleteModal = ccpaDeletionModalState && !isDeleted;
  const showDeleteModal = deletionStatus === 'allowed' && !isDeleted;
  const showArchivedModal =
    deletionStatus === 'not_allowed' && !ccpaDeletionModalState && !isArchived;

  return {
    isDeleted,
    isArchived,
    isLoading,
    showDeleteModal,
    showArchivedModal,
    showCCPADeleteModal,
    clientName,
    handleDeleteClient,
    handleArchiveClient,
    setCCPADeletionModalState,
  };
}
